import {
  formatObservationDateTime,
  hasExecutedPeriod,
  getObservationMapPeriod,
} from "../../helpers/observationDateUtils";
import { serviceOrderTypeLabel } from "../../constants/serviceOrderTypes";
import { machineIdentsFromPlan } from "../PendingSchedules/serviceOrderProducts";
import { sameMachineIdent } from "./dashboardMapUtils";

const NOTE_CHAR_LIMIT = 4000;

export function escapeTrailHtml(value) {
  return String(value == null ? "" : value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function dayKeyInZone(value, timeZone) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: timeZone || undefined,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(date);
  } catch (error) {
    return new Intl.DateTimeFormat("en-CA", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(date);
  }
}

export function rangeCoversDay(pointTime, start, end, timeZone, openEnded) {
  const day = dayKeyInZone(pointTime, timeZone);
  const startDay = dayKeyInZone(start, timeZone);
  if (!day || !startDay || day < startDay) return false;
  if (end) {
    const endDay = dayKeyInZone(end, timeZone);
    return endDay ? day <= endDay : day === startDay;
  }
  if (openEnded) {
    const today = dayKeyInZone(new Date(), timeZone);
    return Boolean(today) && day <= today;
  }
  return day === startDay;
}

export function trailMachineIdent(trail) {
  if (!trail) return "";
  return String(trail.machineIdent || "").trim();
}

function operatorAssigned(obs, trail) {
  if (!obs || !trail) return false;
  const operators = obs.assigned_operators || [];
  if (
    trail.operatorId != null &&
    trail.operatorId !== "" &&
    operators.some((operator) => Number(operator && operator.id) === Number(trail.operatorId))
  ) {
    return true;
  }
  const wanted = String(trail.operatorName || "")
    .trim()
    .toLowerCase();
  if (!wanted) return false;
  return operators.some((operator) => {
    const name = String((operator && (operator.name || operator.nome)) || "")
      .trim()
      .toLowerCase();
    return name && name === wanted;
  });
}

function planObject(obs) {
  const plan = obs && obs.service_order_plan;
  if (!plan) return {};
  if (typeof plan === "string") {
    try {
      const parsed = JSON.parse(plan);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch (error) {
      return {};
    }
  }
  return plan;
}

function isDayReport(obs) {
  const plan = planObject(obs);
  return Boolean(plan.day_report_uuid || plan.origin === "relato_dia");
}

function dayReportMatchesTrail(obs, trail) {
  const idents = machineIdentsFromPlan(planObject(obs));
  const machine = trailMachineIdent(trail);
  if (trail.kind === "operator") {
    return operatorAssigned(obs, trail);
  }
  if (idents.length) {
    return Boolean(machine) && idents.some((ident) => sameMachineIdent(ident, machine));
  }
  return operatorAssigned(obs, trail);
}

function fieldExecutionsOf(obs) {
  if (!obs) return [];
  const raw = obs.field_executions || obs.fieldExecutions || [];
  return Array.isArray(raw) ? raw : [];
}

function fieldExecutionCoversPoint(entry, pointTime) {
  if (!entry || !pointTime) return false;
  const start = entry.startedAt || entry.started_at;
  if (!start) return false;
  const stopped = entry.stoppedAt || entry.stopped_at;
  const open =
    !stopped ||
    String(entry.status || "")
      .trim()
      .toLowerCase() === "gravando";
  return pointInside(pointTime, start, open ? null : stopped);
}

function fieldExecutionMatchesTrail(obs, trail, pointTime) {
  const machine = trailMachineIdent(trail);
  return fieldExecutionsOf(obs).some((entry) => {
    if (!fieldExecutionCoversPoint(entry, pointTime)) return false;
    const entryMachine = String(entry.machineId || entry.machine_id || "").trim();
    if (trail.kind === "operator") {
      if (
        trail.operatorId != null &&
        trail.operatorId !== "" &&
        Number(entry.operatorId || entry.operator_id) === Number(trail.operatorId)
      ) {
        return true;
      }
      return operatorAssigned(obs, trail);
    }
    if (entryMachine && machine) {
      return sameMachineIdent(entryMachine, machine);
    }
    return serviceOrderOnMachine(obs, trail);
  });
}

export function serviceOrderMatchesTrail(obs, trail, pointTime, timeZone) {
  if (!obs || !trail || !pointTime) return false;

  // OS assumida / trecho gravado no app: vale o que o operador registrou.
  if (fieldExecutionMatchesTrail(obs, trail, pointTime)) {
    return true;
  }
  const period = getObservationMapPeriod(obs);
  if (
    !period ||
    !rangeCoversDay(pointTime, period.start, period.end, timeZone, false)
  ) {
    return false;
  }

  if (isDayReport(obs)) {
    return dayReportMatchesTrail(obs, trail);
  }

  const idents = machineIdentsFromPlan(obs.service_order_plan);
  const machine = trailMachineIdent(trail);
  if (idents.length) {
    return Boolean(machine) && idents.some((ident) => sameMachineIdent(ident, machine));
  }
  return operatorAssigned(obs, trail);
}

function operationMachineIdent(operation) {
  if (!operation) return "";
  if (operation.machine_name) return String(operation.machine_name).trim();
  if (operation.machine && typeof operation.machine === "object") {
    return String(operation.machine.ident || "").trim();
  }
  return "";
}

function operationOperatorId(operation) {
  if (!operation) return null;
  if (operation.operator && typeof operation.operator === "object") {
    return operation.operator.id;
  }
  if (typeof operation.operator === "number" || typeof operation.operator === "string") {
    return operation.operator;
  }
  return operation.operator_id;
}

function operationOperatorName(operation) {
  if (!operation) return "";
  if (operation.operator_name) return String(operation.operator_name).trim();
  if (operation.operator && typeof operation.operator === "object") {
    return String(operation.operator.nome || operation.operator.name || "").trim();
  }
  return "";
}

export function operationMatchesTrail(operation, trail, pointTime, timeZone) {
  if (!operation || !trail || !pointTime) return false;
  const openEnded = !operation.fineshed && !operation.fineshed_at;
  if (
    !rangeCoversDay(
      pointTime,
      operation.dt_ini,
      operation.fineshed_at,
      timeZone,
      openEnded
    )
  ) {
    return false;
  }

  const machine = trailMachineIdent(trail);
  const operationMachine = operationMachineIdent(operation);
  if (operationMachine) {
    return Boolean(machine) && sameMachineIdent(operationMachine, machine);
  }

  const operatorId = operationOperatorId(operation);
  if (
    trail.operatorId != null &&
    trail.operatorId !== "" &&
    operatorId != null &&
    operatorId !== "" &&
    Number(operatorId) === Number(trail.operatorId)
  ) {
    return true;
  }
  const wanted = String(trail.operatorName || "")
    .trim()
    .toLowerCase();
  const name = operationOperatorName(operation).toLowerCase();
  return Boolean(wanted) && wanted === name;
}

function clipNote(value) {
  const text = String(value || "").trim();
  if (text.length <= NOTE_CHAR_LIMIT) return text;
  return text.slice(0, NOTE_CHAR_LIMIT).trim() + "…";
}

const VIEW_ORDER_ICON =
  '<svg viewBox="0 0 576 512" width="14" height="14" aria-hidden="true"><path fill="currentColor" d="M572.52 241.4C518.29 135.59 410.93 64 288 64S57.68 135.64 3.48 241.41a32.35 32.35 0 0 0 0 29.19C57.71 376.41 165.07 448 288 448s230.32-71.64 284.52-177.41a32.35 32.35 0 0 0 0-29.19zM288 400a144 144 0 1 1 144-144 143.93 143.93 0 0 1-144 144zm0-240a95.31 95.31 0 0 0-25.31 3.79 47.85 47.85 0 0 1-66.9 66.9A95.78 95.78 0 1 0 288 160z"/></svg>';

const EDIT_WORK_ICON =
  '<svg viewBox="0 0 512 512" width="13" height="13" aria-hidden="true"><path fill="currentColor" d="M290.74 93.24l128.02 128.02-277.99 277.99-114.14 12.6C11.35 513.54-1.56 500.62.14 485.34l12.7-114.22 277.9-277.88zm207.2-19.06l-60.11-60.11c-18.75-18.75-49.16-18.75-67.91 0l-56.55 56.55 128.02 128.02 56.55-56.55c18.75-18.76 18.75-49.17 0-67.91z"/></svg>';

function orderNote(obs) {
  const description = String((obs && obs.description) || "").trim();
  const rawTitle = String(
    (obs && (obs.operationName || obs.operation_name)) || ""
  ).trim();
  const shortTitle = rawTitle.split(" — ")[0].trim();
  if (description) {
    if (
      shortTitle &&
      shortTitle !== description &&
      !description.startsWith(shortTitle) &&
      !rawTitle.includes(description)
    ) {
      return clipNote(`${shortTitle}\n${description}`);
    }
    if (shortTitle && rawTitle.includes(description) && shortTitle !== description) {
      return clipNote(`${shortTitle}\n${description}`);
    }
    return clipNote(description);
  }
  return clipNote(shortTitle || rawTitle);
}

function pointInside(pointTime, start, end) {
  const target = new Date(pointTime).getTime();
  const startMs = new Date(start).getTime();
  if (!Number.isFinite(target) || !Number.isFinite(startMs) || target < startMs) {
    return false;
  }
  if (!end) return true;
  const endMs = new Date(end).getTime();
  return Number.isFinite(endMs) && target <= endMs;
}

function operationCoversPoint(operation, pointTime) {
  if (!operation) return false;
  const openEnded = !operation.fineshed && !operation.fineshed_at;
  return pointInside(
    pointTime,
    operation.dt_ini,
    openEnded ? null : operation.fineshed_at
  );
}

function orderCoversPoint(obs, pointTime) {
  const period = getObservationMapPeriod(obs);
  if (!period) return false;
  return pointInside(pointTime, period.start, period.end);
}

function serviceOrderOnMachine(obs, trail) {
  if (isDayReport(obs)) {
    return dayReportMatchesTrail(obs, trail);
  }
  const machine = trailMachineIdent(trail);
  const executionIdents = (
    Array.isArray(obs && obs.field_executions) ? obs.field_executions : []
  )
    .map((entry) => String((entry && entry.machineId) || "").trim())
    .filter(Boolean);
  if (machine && executionIdents.some((ident) => sameMachineIdent(ident, machine))) {
    return true;
  }
  const idents = machineIdentsFromPlan(obs && obs.service_order_plan);
  if (idents.length) {
    return Boolean(machine) && idents.some((ident) => sameMachineIdent(ident, machine));
  }
  return operatorAssigned(obs, trail);
}

function hasLiveFieldWork(obs, pointTime, trail) {
  if (trail) return fieldExecutionMatchesTrail(obs, trail, pointTime);
  return fieldExecutionsOf(obs).some((entry) =>
    fieldExecutionCoversPoint(entry, pointTime)
  );
}

function pickSavedOrder(list, pointTime, trail) {
  if (!list || !list.length) return null;
  return [...list].sort((left, right) => {
    const leftLive = hasLiveFieldWork(left, pointTime, trail) ? 1 : 0;
    const rightLive = hasLiveFieldWork(right, pointTime, trail) ? 1 : 0;
    if (leftLive !== rightLive) return rightLive - leftLive;
    const leftReport = isDayReport(left) ? 1 : 0;
    const rightReport = isDayReport(right) ? 1 : 0;
    if (leftReport !== rightReport) return rightReport - leftReport;
    const leftHit = orderCoversPoint(left, pointTime) ? 1 : 0;
    const rightHit = orderCoversPoint(right, pointTime) ? 1 : 0;
    if (leftHit !== rightHit) return rightHit - leftHit;
    const leftConfirmed = left && left.confirmed ? 1 : 0;
    const rightConfirmed = right && right.confirmed ? 1 : 0;
    if (leftConfirmed !== rightConfirmed) return rightConfirmed - leftConfirmed;
    const leftStart = new Date(
      (left && (left.executed_start_date || left.start_date)) || 0
    ).getTime();
    const rightStart = new Date(
      (right && (right.executed_start_date || right.start_date)) || 0
    ).getTime();
    return rightStart - leftStart;
  })[0];
}

function draftEnd(start, end) {
  const startDate = new Date(start);
  const endDate = end ? new Date(end) : null;
  if (
    endDate &&
    !Number.isNaN(endDate.getTime()) &&
    !Number.isNaN(startDate.getTime()) &&
    endDate.getTime() > startDate.getTime()
  ) {
    return endDate.toISOString();
  }
  if (Number.isNaN(startDate.getTime())) return end || start;
  const plus = new Date(startDate.getTime());
  plus.setHours(plus.getHours() + 1);
  return plus.toISOString();
}

export function selectTrailWork({
  trail,
  pointTime,
  serviceOrders,
  operations,
  timeZone,
} = {}) {
  const operation =
    (operations || [])
      .filter(
        (item) =>
          operationMatchesTrail(item, trail, pointTime, timeZone) &&
          operationCoversPoint(item, pointTime)
      )
      .sort((left, right) => {
        // Prefere operação de OS (não monitoramento automático).
        const leftAuto = left && left.auto_monitoring ? 1 : 0;
        const rightAuto = right && right.auto_monitoring ? 1 : 0;
        if (leftAuto !== rightAuto) return leftAuto - rightAuto;
        return new Date(right.dt_ini).getTime() - new Date(left.dt_ini).getTime();
      })[0] || null;

  const onMachine = (serviceOrders || []).filter((obs) =>
    serviceOrderOnMachine(obs, trail)
  );
  // OS assumida no app tem prioridade sobre a operação de monitoramento.
  let pool = (serviceOrders || []).filter((obs) =>
    fieldExecutionMatchesTrail(obs, trail, pointTime)
  );
  if (!pool.length && operation && !operation.auto_monitoring) {
    pool = (serviceOrders || []).filter(
      (obs) => Number(obs && obs.operation) === Number(operation.id)
    );
  }
  if (!pool.length) {
    pool = onMachine.filter(
      (obs) =>
        orderCoversPoint(obs, pointTime) ||
        fieldExecutionMatchesTrail(obs, trail, pointTime) ||
        (isDayReport(obs) && dayReportMatchesTrail(obs, trail))
    );
  }
  const order = pickSavedOrder(pool, pointTime, trail);
  const start = (operation && operation.dt_ini) || pointTime;
  return {
    order,
    operation,
    draft: {
      operationId: operation ? operation.id : null,
      start,
      end: draftEnd(start, operation && operation.fineshed_at),
      operatorId: trail && trail.operatorId != null ? trail.operatorId : null,
      machineIdent: trailMachineIdent(trail),
    },
  };
}

export function mergeTrailOperations(periodOperations, openOperations) {
  const byId = new Map();
  [...(openOperations || []), ...(periodOperations || [])].forEach((operation) => {
    if (!operation || operation.id == null) return;
    byId.set(operation.id, operation);
  });
  return Array.from(byId.values());
}

export function trailSubjectHtml(trail, t) {
  const translate = t || ((text) => text);
  if (!trail) return "";
  const operatorName = String(trail.operatorName || "").trim();
  const machine = trailMachineIdent(trail);
  const lines = [];
  const namedOperator = operatorName || (trail.kind !== "machine" ? String(trail.name || "").trim() : "");
  if (namedOperator) {
    lines.push(
      '<div class="agri-point-details-iw__row"><strong>' +
        escapeTrailHtml(translate("Operador")) +
        ":</strong> " +
        escapeTrailHtml(namedOperator) +
        "</div>"
    );
  }
  if (machine) {
    lines.push(
      '<div class="agri-point-details-iw__row"><strong>' +
        escapeTrailHtml(translate("Máquina")) +
        ":</strong> " +
        escapeTrailHtml(machine) +
        "</div>"
    );
  } else if (!namedOperator && trail.name) {
    lines.push(
      '<div class="agri-point-details-iw__who">' +
        escapeTrailHtml(trail.name) +
        "</div>"
    );
  }
  return lines.join("");
}

function legacyHeadline(obs) {
  const rawTitle = String(
    (obs && (obs.operationName || obs.operation_name)) || ""
  ).trim();
  return rawTitle.split(" — ")[0].trim();
}

function orderProductsText(obs) {
  return String(
    (obs && (obs.products_used || obs.productsUsed || obs.product)) || ""
  ).trim();
}

function orderHeadlineParts(obs, translate) {
  const typeLabel = serviceOrderTypeLabel(obs && obs.service_type, translate);
  const title = String((obs && obs.operation_title) || "").trim();
  const description = String((obs && obs.description) || "").trim();
  const legacy = legacyHeadline(obs);
  const typeLine =
    typeLabel ||
    (legacy && legacy !== title && legacy !== description ? legacy : "");
  return { typeLine, title, description, products: orderProductsText(obs) };
}

function orderStatusLabel(obs, translate) {
  if (hasExecutedPeriod(obs) || (obs && obs.confirmed && obs.executed_end_date)) {
    return translate("Concluída");
  }
  const executions = fieldExecutionsOf(obs);
  const recording = executions.some((entry) => {
    const status = String((entry && entry.status) || "")
      .trim()
      .toLowerCase();
    const stopped = entry && (entry.stoppedAt || entry.stopped_at);
    return status === "gravando" || !stopped;
  });
  if (recording || (obs && obs.confirmed)) {
    return translate("Em andamento");
  }
  return translate("Pendente");
}

function orderExecutedRange(obs) {
  if (hasExecutedPeriod(obs)) {
    return {
      start: obs.executed_start_date,
      end: obs.executed_end_date,
    };
  }
  const executions = fieldExecutionsOf(obs);
  let start = null;
  let end = null;
  let open = false;
  executions.forEach((entry) => {
    const started = entry && (entry.startedAt || entry.started_at);
    const stopped = entry && (entry.stoppedAt || entry.stopped_at);
    const status = String((entry && entry.status) || "")
      .trim()
      .toLowerCase();
    if (started && (!start || new Date(started) < new Date(start))) {
      start = started;
    }
    if (!stopped || status === "gravando") {
      open = true;
      return;
    }
    if (!end || new Date(stopped) > new Date(end)) {
      end = stopped;
    }
  });
  if (!start) return null;
  return { start, end: open ? null : end, open };
}

/** Períodos prevista / efetivada — só no Ver mais. */
function orderScheduleHtml(obs, translate) {
  if (!obs) return "";
  const rows = [];
  if (obs.start_date || obs.end_date) {
    rows.push(
      '<div class="agri-point-work__schedule-row">' +
        '<span class="agri-point-work__schedule-label">' +
        escapeTrailHtml(translate("Ordem prevista")) +
        ":</span> " +
        '<span class="agri-point-work__schedule-values">' +
        escapeTrailHtml(formatObservationDateTime(obs.start_date)) +
        " → " +
        escapeTrailHtml(formatObservationDateTime(obs.end_date)) +
        "</span></div>"
    );
  }
  const executed = orderExecutedRange(obs);
  let executedText = translate("Ainda não iniciada");
  if (executed && executed.start) {
    executedText =
      formatObservationDateTime(executed.start) +
      " → " +
      (executed.open || !executed.end
        ? translate("em andamento")
        : formatObservationDateTime(executed.end));
  } else if (obs.confirmed) {
    executedText = translate("Período executado não informado");
  }
  rows.push(
    '<div class="agri-point-work__schedule-row">' +
      '<span class="agri-point-work__schedule-label">' +
      escapeTrailHtml(translate("Ordem efetivada")) +
      ":</span> " +
      '<span class="agri-point-work__schedule-values">' +
      escapeTrailHtml(executedText) +
      "</span></div>"
  );
  rows.push(
    '<div class="agri-point-work__schedule-row">' +
      '<span class="agri-point-work__schedule-label">' +
      escapeTrailHtml(translate("Status")) +
      ":</span> " +
      '<span class="agri-point-work__schedule-values">' +
      escapeTrailHtml(orderStatusLabel(obs, translate)) +
      "</span></div>"
  );
  return (
    '<div class="agri-point-work__schedule">' + rows.join("") + "</div>"
  );
}

/** Resumo do popup: só tipo/título. Detalhes (períodos/obs./produtos) no Ver mais. */
function orderWorkHtml(obs, translate) {
  const { typeLine, title, description, products } = orderHeadlineParts(
    obs,
    translate
  );
  const summary = [];
  if (typeLine) {
    summary.push(
      '<div class="agri-point-work__type">' + escapeTrailHtml(typeLine) + "</div>"
    );
  }
  if (title && title !== typeLine) {
    summary.push(
      '<div class="agri-point-work__title">' + escapeTrailHtml(title) + "</div>"
    );
  }
  if (!summary.length) {
    const fallback = orderNote(obs);
    if (fallback) {
      summary.push(
        '<div class="agri-point-work__title">' +
          escapeTrailHtml(fallback.split("\n")[0] || fallback) +
          "</div>"
      );
    }
  }

  const shown = new Set([typeLine, title].filter(Boolean));
  const details = [orderScheduleHtml(obs, translate)];
  if (description && !shown.has(description)) {
    details.push(
      '<div class="agri-point-work__text">' +
        escapeTrailHtml(clipNote(description)) +
        "</div>"
    );
  }
  if (products && !shown.has(products) && products !== description) {
    details.push(
      '<div class="agri-point-work__text">' +
        escapeTrailHtml(clipNote(products)) +
        "</div>"
    );
  }

  const detailHtml = details.filter(Boolean).join("");
  if (!summary.length && !detailHtml) return "";
  return (
    summary.join("") +
    '<div class="agri-point-work__details" hidden>' +
    detailHtml +
    "</div>"
  );
}

function orderHasExtraDetails(obs) {
  return Boolean(obs && obs.id);
}

function createActionHtml(translate) {
  const editLabel = translate("Editar trabalho");
  return (
    '<div class="agri-point-work__actions">' +
    '<button type="button" class="agri-point-work__icon" data-trail-create-order="1" title="' +
    escapeTrailHtml(editLabel) +
    '" aria-label="' +
    escapeTrailHtml(editLabel) +
    '">' +
    EDIT_WORK_ICON +
    "</button></div>"
  );
}

function orderActionsHtml(obs, translate) {
  const id = Number(obs && obs.id);
  if (!Number.isFinite(id) || !id) return "";
  const hasMore = orderHasExtraDetails(obs);
  const moreLabel = translate("Ver mais");
  const viewLabel = translate("Ver ordem de serviço");
  const editLabel = translate("Editar trabalho");
  const moreButton = hasMore
    ? '<button type="button" class="agri-point-work__more" data-trail-toggle-details="1" aria-expanded="false" title="' +
      escapeTrailHtml(moreLabel) +
      '" aria-label="' +
      escapeTrailHtml(moreLabel) +
      '">' +
      '<span class="agri-point-details-iw__eye" aria-hidden="true">&#128065;</span>' +
      '<span class="agri-point-work__more-label">' +
      escapeTrailHtml(moreLabel) +
      "</span></button>"
    : "";
  return (
    '<div class="agri-point-work__actions">' +
    moreButton +
    '<button type="button" class="agri-point-work__icon" data-trail-edit-order="' +
    id +
    '" title="' +
    escapeTrailHtml(editLabel) +
    '" aria-label="' +
    escapeTrailHtml(editLabel) +
    '">' +
    EDIT_WORK_ICON +
    "</button></div>" +
    '<button type="button" class="agri-point-work__open-order" data-trail-view-order="' +
    id +
    '" title="' +
    escapeTrailHtml(viewLabel) +
    '" aria-label="' +
    escapeTrailHtml(viewLabel) +
    '">' +
    VIEW_ORDER_ICON +
    "<span>" +
    escapeTrailHtml(viewLabel) +
    "</span></button>"
  );
}

function noteBlock(label, text, extras) {
  const bodyHtml = extras && extras.bodyHtml;
  const actionsHtml = (extras && extras.actionsHtml) || "";
  const body = String(text || "").trim();
  if (!body && !bodyHtml) return "";
  return (
    '<div class="agri-point-work">' +
    '<div class="agri-point-work__label">' +
    escapeTrailHtml(label) +
    "</div>" +
    (bodyHtml ||
      '<div class="agri-point-work__text">' + escapeTrailHtml(body) + "</div>") +
    actionsHtml +
    "</div>"
  );
}

function orderWorkBlock(obs, translate) {
  return noteBlock(translate("Operação"), "", {
    bodyHtml: orderWorkHtml(obs, translate),
    actionsHtml: orderActionsHtml(obs, translate),
  });
}

function dayReportOnPoint(serviceOrders, trail, pointTime, timeZone) {
  return (serviceOrders || [])
    .filter(
      (obs) =>
        isDayReport(obs) &&
        serviceOrderMatchesTrail(obs, trail, pointTime, timeZone) &&
        orderCoversPoint(obs, pointTime)
    )
    .sort(
      (left, right) =>
        new Date(right.executed_start_date || right.start_date).getTime() -
        new Date(left.executed_start_date || left.start_date).getTime()
    )[0];
}

export function trailWorkNotesHtml({
  trail,
  pointTime,
  serviceOrders,
  operations,
  timeZone,
  t,
} = {}) {
  const translate = t || ((text) => text);
  const selected = selectTrailWork({
    trail,
    pointTime,
    serviceOrders,
    operations,
    timeZone,
  });
  const report = dayReportOnPoint(serviceOrders, trail, pointTime, timeZone);
  const blocks = [];
  if (selected.order && !(report && selected.order.id === report.id)) {
    blocks.push(orderWorkBlock(selected.order, translate));
  }
  if (report) blocks.push(orderWorkBlock(report, translate));
  if (blocks.length) return blocks.join("");
  return noteBlock(
    translate("Operação"),
    translate("Sem ordem de serviço neste rastro."),
    { actionsHtml: createActionHtml(translate) }
  );
}
